'use client';

import { useGSAP } from '@gsap/react';
import clsx from 'clsx';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';

import CustomSlider from '@/components/Hero/CustomSlider';
import PageBreadcrumbs from '@/components/PageBreadcrumbs';
import { Typography } from '@/components/shared/Typography';
import StrapiRichContent from '@/components/StrapiRichContent';
import useDevice from '@/hooks/useDevice';
import { blocksContentToAltText } from '@/lib/blocks-content';
import { breadcrumbTrails } from '@/lib/breadcrumbs';
import { gsap } from '@/lib/gsap';

import css from './style.module.scss';
import { WebsiteProdHeroDto } from './types';

interface Props {
	data: WebsiteProdHeroDto;
	isIframe?: boolean;
}

const WebsiteProdHero = ({ data, isIframe }: Props) => {
	const device = useDevice();
	const isMobile = device === 'mobile';

	const wrapperRef = useRef<HTMLDivElement>(null);
	const parallaxImageRef = useRef<HTMLDivElement>(null);
	const iframeRef = useRef<HTMLIFrameElement>(null);
	const breadcrumbsRef = useRef<HTMLDivElement>(null);

	const [isImageLoaded, setIsImageLoaded] = useState(false);

	useGSAP(
		() => {
			if (!wrapperRef.current) return;

			const titleLines = wrapperRef.current.querySelectorAll(`.${css.title} p`);
			const bottom = wrapperRef.current.querySelector(`.${css.bottomContainer}`);
			const breadcrumbs = breadcrumbsRef.current;

			gsap.set([titleLines, bottom], {
				opacity: 0,
				y: 40,
				filter: 'blur(10px)'
			});

			if (breadcrumbs)
				gsap.set(breadcrumbs, {
					opacity: 0,
					y: 40,
					filter: 'blur(10px)'
				});

			gsap.set(parallaxImageRef.current, {
				opacity: 0,
				scale: 1.1
			});

			if (!isImageLoaded) return;

			const tl = gsap.timeline({
				defaults: { ease: 'power3.out', duration: 1 }
			});

			tl.to(parallaxImageRef.current, {
				opacity: 1,
				scale: 1,
				duration: 1.5
			})
				.to(
					titleLines,
					{
						opacity: 1,
						y: 0,
						filter: 'blur(0px)',
						stagger: 0.15,
						duration: 1.1
					},
					'-=0.8'
				)
				.to(
					bottom,
					{
						opacity: 1,
						y: -32,
						yPercent: -100,
						filter: 'blur(0px)',
						ease: 'power2.out'
					},
					'-=0.8'
				);

			if (breadcrumbs)
				tl.to(
					breadcrumbs,
					{
						opacity: 1,
						y: 0,
						filter: 'blur(0px)',
						duration: 1,
						ease: 'power2.out'
					},
					'-=0.6'
				);

			if (isIframe) return;

			const shine = parallaxImageRef.current?.querySelector(`.${css.shine}`);

			gsap.to(parallaxImageRef.current, {
				yPercent: -20,
				rotationX: 4,
				ease: 'none',
				scrollTrigger: {
					trigger: wrapperRef.current,
					start: 'bottom bottom',
					end: 'bottom top',
					scrub: 0.5
				}
			});

			if (shine)
				gsap.fromTo(
					shine,
					{ backgroundPosition: '160% 0%' },
					{
						backgroundPosition: '-120% 0%',
						ease: 'none',
						scrollTrigger: {
							trigger: wrapperRef.current,
							start: 'bottom bottom',
							end: 'bottom top',
							scrub: 0.5
						}
					}
				);
		},
		{ scope: wrapperRef, dependencies: [isIframe, isImageLoaded] }
	);

	useEffect(() => {
		if (isIframe) {
			const handler = (event: MessageEvent) => {
				if (event.data?.type === 'SCROLL_SYNC') {
					const { progress } = event.data;
					const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
					window.scrollTo(0, progress * scrollHeight);
				}
			};
			window.addEventListener('message', handler);
			return () => window.removeEventListener('message', handler);
		}
		let ticking = false;
		const onScroll = () => {
			if (!ticking) {
				window.requestAnimationFrame(() => {
					if (window.scrollY > window.innerHeight * 1.5) {
						ticking = false;
						return;
					}

					if (iframeRef.current?.contentWindow) {
						const progress =
							window.scrollY / (document.documentElement.scrollHeight - window.innerHeight);
						iframeRef.current.contentWindow.postMessage({ type: 'SCROLL_SYNC', progress }, '*');
					}
					ticking = false;
				});
				ticking = true;
			}
		};
		window.addEventListener('scroll', onScroll);
		return () => window.removeEventListener('scroll', onScroll);
	}, [isIframe]);

	return (
		<section ref={wrapperRef} className={clsx('wrapper', css.wrapper, isIframe && css.isIframe)}>
			<div className={css.content}>
				<PageBreadcrumbs
					ref={breadcrumbsRef}
					items={breadcrumbTrails.servicePage('Website Production')}
					wrapperClassName={css.breadcrumbsWrap}
					className={css.breadcrumbs}
				/>

				<Typography uppercase variant="h1" tag="h1" className={css.title}>
					<StrapiRichContent content={data.title} />
				</Typography>
			</div>

			<div className={css.bottomContainer}>
				<div className={css.sliderWrapper}>
					<CustomSlider items={data.slider} />
				</div>
			</div>

			<div ref={parallaxImageRef} className={css.parallaxWrapper}>
				<Image
					preload
					className={css.parallaxImg}
					src="/assets/images/website-prod-page/hero/car-parallax.webp"
					width={1680}
					height={1440}
					alt=""
					onLoad={() => setIsImageLoaded(true)}
				/>

				<div className={css.screenWrapper}>
					<div className={css.screenInner}>
						{isIframe || isMobile ? (
							data.imageInScreen?.url ? (
								<Image
									className={css.screenContent}
									src={data.imageInScreen.url}
									width={data.imageInScreen.width}
									height={data.imageInScreen.height}
									alt={data.imageInScreen.alternativeText || blocksContentToAltText(data.title)}
								/>
							) : null
						) : isImageLoaded ? (
							<iframe
								ref={iframeRef}
								className={css.screenContent}
								width="100%"
								height="100%"
								src={`${window.location.origin}/embed/website-production`}
								loading="lazy"
							/>
						) : null}
					</div>
				</div>

				<div className={css.shine} />
			</div>
		</section>
	);
};

export default WebsiteProdHero;
